import { Control, Controller, FieldArrayWithId } from 'react-hook-form'

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Button, Grid, IconButton, Paper, TextField, Typography } from '@mui/material'
import { SANITIZE_HTML_OPTIONS } from 'common/constants'
import sanitizeHtml from 'sanitize-html'
import SunEditor, { buttonList } from 'suneditor-react'

import s from '../VideoMainForm.module.css'

import { VideoMainFormValues } from '../videoMainForm.types'

type Props = {
  control: Control<VideoMainFormValues>
  fields: FieldArrayWithId<VideoMainFormValues, 'locales', 'id'>[]
  onAdd: () => void
  onRemove: (index: number) => void
}

export const VideoLocalesSection = ({ control, fields, onAdd, onRemove }: Props) => {
  return (
    <Paper className={s.section} variant={'outlined'}>
      <div className={s.sectionHeader}>
        <Typography variant={'h6'}>Локали</Typography>
        <Button
          onClick={onAdd}
          size={'small'}
          startIcon={<AddIcon />}
          type={'button'}
          variant={'outlined'}
        >
          Добавить locale
        </Button>
      </div>

      <Typography className={s.sectionHint} variant={'body2'}>
        Для каждой locale укажи название и, если нужно, описание с SEO-описанием.
      </Typography>

      <div className={s.cards}>
        {fields.map((field, index) => (
          <div className={s.card} key={field.id}>
            <div className={s.cardHeader}>
              <Typography variant={'subtitle1'}>{`Locale #${index + 1}`}</Typography>
              <IconButton
                disabled={fields.length === 1}
                onClick={() => onRemove(index)}
                size={'small'}
                type={'button'}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </div>

            <Grid container spacing={2}>
              <Grid item md={3} xs={12}>
                <Controller
                  control={control}
                  name={`locales.${index}.locale`}
                  render={({ field: localeField }) => (
                    <TextField
                      fullWidth
                      label={'Locale'}
                      placeholder={'ru'}
                      required
                      {...localeField}
                    />
                  )}
                />
              </Grid>

              <Grid item md={9} xs={12}>
                <Controller
                  control={control}
                  name={`locales.${index}.name`}
                  render={({ field: nameField, fieldState }) => (
                    <TextField
                      error={!!fieldState.error}
                      fullWidth
                      helperText={fieldState.error?.message}
                      label={'Название видео'}
                      required
                      {...nameField}
                    />
                  )}
                  rules={{
                    maxLength: {
                      message: 'Максимальная длина 250 символов',
                      value: 250,
                    },
                    validate: value => value.trim().length > 0 || 'Название обязательно',
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`locales.${index}.description`}
                  render={({ field: descriptionField }) => (
                    <div className={s.editorContainer}>
                      <Typography className={s.editorLabel} variant={'body2'}>
                        Описание
                      </Typography>
                      <SunEditor
                        onChange={value =>
                          descriptionField.onChange(sanitizeHtml(value, SANITIZE_HTML_OPTIONS))
                        }
                        setContents={descriptionField.value || ''}
                        setDefaultStyle={'p {margin: 0px;}'}
                        setOptions={{
                          buttonList: [...buttonList.complex, ['textStyle']],
                          height: '200px',
                        }}
                      />
                    </div>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`locales.${index}.seoDescription`}
                  render={({ field: seoField }) => (
                    <TextField
                      fullWidth
                      label={'SEO описание'}
                      minRows={3}
                      multiline
                      {...seoField}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </div>
        ))}
      </div>
    </Paper>
  )
}
